import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Gauge } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from '@/lib/utils';
import { MarketingPageShell } from './components/MarketingPageShell';
import { KpiChange, Panel } from './components/marketing-ui';
import { formatKpiValue } from './fixtures';
import { PUBLIC_MAX_RANGE_DAYS, PUBLIC_RANGE_PRESETS, ownedWindowBounds } from './posting-insights-data';
import { isUnsynced } from './posting-insights-live';
import {
  loadPublicRangePreference,
  savePublicRangePreference,
  servedWindowLabel,
  utcDayKey,
  utcDayTick,
} from './performance-range-storage';
import { usePerformanceOwnedQuery, usePerformanceTrackedQuery } from '@/client/api/marketing/radar';
import type { ApiOwnedDailyPoint, ApiOwnedKpi, ApiTrackedKpi } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';

type SparkPoint = { date: string; value: number | null };

function sumNullable(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null && b == null) return null;
  return (a ?? 0) + (b ?? 0);
}

function ownedDays(series: ApiOwnedDailyPoint[]): {
  date: string;
  engagement: number | null;
  reach: number | null;
  impressions: number | null;
  posts: number | null;
  clicks: number | null;
}[] {
  const byDate = new Map<
    string,
    { date: string; engagement: number | null; reach: number | null; impressions: number | null; posts: number | null; clicks: number | null }
  >();
  for (const row of series) {
    const date = utcDayKey(row.date);
    if (!date) continue;
    const cur = byDate.get(date) ?? {
      date,
      engagement: null,
      reach: null,
      impressions: null,
      posts: null,
      clicks: null,
    };
    cur.engagement = sumNullable(cur.engagement, row.engagement);
    cur.reach = sumNullable(cur.reach, row.reach);
    cur.impressions = sumNullable(cur.impressions, row.impressions);
    cur.posts = sumNullable(cur.posts, row.posts);
    cur.clicks = sumNullable(cur.clicks, row.clicks);
    byDate.set(date, cur);
  }
  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

function ownedSpark(kpi: ApiOwnedKpi, days: ReturnType<typeof ownedDays>): SparkPoint[] {
  return days.map((d) => {
    if (kpi.id === 'kpi_reach') return { date: d.date, value: d.reach };
    if (kpi.id === 'kpi_posts') return { date: d.date, value: d.posts };
    if (kpi.id === 'kpi_ctr') {
      if (d.clicks == null || d.impressions == null || d.impressions === 0) return { date: d.date, value: null };
      return { date: d.date, value: d.clicks / d.impressions };
    }
    const denom = kpi.basis === 'impressions' ? d.impressions : d.reach;
    if (d.engagement == null || denom == null || denom === 0) return { date: d.date, value: null };
    return { date: d.date, value: d.engagement / denom };
  });
}

function KpiSpark({
  series,
  ratio,
  name,
  stroke,
}: {
  series: SparkPoint[];
  ratio: boolean;
  name: string;
  stroke: string;
}) {
  const known = series.filter((p) => p.value != null);
  if (known.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-xs text-gray-400">
        No daily series in this window
      </div>
    );
  }
  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={utcDayTick} />
          <YAxis
            tick={{ fontSize: 10 }}
            width={ratio ? 44 : 36}
            tickFormatter={ratio ? (v: number) => `${(v * 100).toFixed(0)}%` : undefined}
          />
          <Tooltip
            labelFormatter={(d: string) => utcDayKey(d) || d}
            formatter={(value) => {
              if (typeof value !== 'number') return ['—', name];
              return [ratio ? `${(value * 100).toFixed(2)}%` : value, name];
            }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={stroke}
            fill={stroke}
            fillOpacity={0.15}
            strokeWidth={1.5}
            dot={{ r: 2, fill: stroke, strokeWidth: 0 }}
            name={name}
            connectNulls={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function KpiTile({
  title,
  basis,
  change,
  metricValue,
  value,
  spark,
  ratio,
  stroke,
}: {
  title: string;
  basis?: string | null;
  change: number | null;
  metricValue: number | null;
  value: ReactNode;
  spark: SparkPoint[];
  ratio: boolean;
  stroke: string;
}) {
  return (
    <Panel
      title={
        <span className="flex flex-col gap-0.5">
          <span>{title}</span>
          {basis ? (
            <span className="text-[11px] font-normal normal-case tracking-normal text-gray-400">
              basis: {basis}
            </span>
          ) : null}
        </span>
      }
      action={<KpiChange change={change} value={metricValue} />}
    >
      <p className="mb-3 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
      {spark.length > 0 ? <KpiSpark series={spark} ratio={ratio} name={title} stroke={stroke} /> : null}
    </Panel>
  );
}

const KpisPage = ({ embedded = false }: { embedded?: boolean }) => {
  const [range, setRange] = useState(() => loadPublicRangePreference());
  const ownedWindow = useMemo(() => ownedWindowBounds(range), [range]);
  const owned = usePerformanceOwnedQuery({
    range_days: ownedWindow.range_days,
    from: ownedWindow.from,
    to: ownedWindow.to,
  });
  const tracked = usePerformanceTrackedQuery({ range_days: range });
  const liveOwned = !isUnsynced(owned.data);

  useEffect(() => {
    savePublicRangePreference(range);
  }, [range]);

  const days = useMemo(() => ownedDays(owned.data?.series ?? []), [owned.data]);
  const ownedTiles = useMemo(
    () => (owned.data?.kpis ?? []).map((kpi) => ({ kpi, series: ownedSpark(kpi, days) })),
    [owned.data, days],
  );
  const trackedKpis = tracked.data?.kpis ?? [];
  const trackedSeries: SparkPoint[] = useMemo(
    () =>
      (tracked.data?.series?.points ?? []).map((p) => ({
        date: utcDayKey(p.date),
        value: p.value ?? null,
      })),
    [tracked.data],
  );

  const rangeControls = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
        {PUBLIC_RANGE_PRESETS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              range === r ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
            )}
          >
            {r}d
          </button>
        ))}
      </div>
      <span className="text-xs tabular-nums text-gray-400">
        {range > PUBLIC_MAX_RANGE_DAYS &&
        owned.data?.range_days != null &&
        tracked.data?.range_days != null &&
        owned.data.range_days !== tracked.data.range_days
          ? `Connected ${servedWindowLabel(owned.data.range_days, range)} · Public ${servedWindowLabel(tracked.data.range_days, range)}`
          : servedWindowLabel(owned.data?.range_days ?? tracked.data?.range_days, range)}
      </span>
    </div>
  );

  const body = (
    <>
      {owned.isError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-950">
          <p className="font-medium">Could not load owned KPIs</p>
          <p className="mt-1 text-red-900/90">
            {getApiErrorMessage(owned.error, 'Check marketing.view and try again.')}
          </p>
        </div>
      ) : null}
      {tracked.isError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-950">
          <p className="font-medium">Could not load public KPIs</p>
          <p className="mt-1 text-red-900/90">
            {getApiErrorMessage(tracked.error, 'Check marketing.view and try again.')}
          </p>
        </div>
      ) : null}

      <p className="mb-4 text-sm text-gray-600">
        Two rails, never one number. Connected is Zernio on accounts we publish from. Public is
        listening coverage (`metrics_public`). Same tile names (engagement rate, CTR) are different
        facts — they stay in separate grids.
      </p>

      {embedded && <div className="mb-4 flex justify-end">{rangeControls}</div>}

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-semibold text-gray-900">Connected</h2>
        {!liveOwned && !owned.isLoading && !owned.isError ? (
          <div className="rounded-xl border border-gray-100 bg-white px-6 py-14 text-center shadow-sm">
            <p className="text-base font-semibold text-gray-900">No connected analytics yet</p>
            <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500">
              Open Owned and sync once. Nothing is invented here when that store is empty.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {ownedTiles.map(({ kpi, series }) => (
              <KpiTile
                key={`owned:${kpi.id}:${kpi.basis ?? 'none'}`}
                title={kpi.label}
                basis={kpi.basis}
                change={kpi.change_pct}
                metricValue={kpi.value}
                value={formatKpiValue(kpi)}
                spark={series}
                ratio={kpi.unit === 'ratio'}
                stroke="#0f172a"
              />
            ))}
            {liveOwned && ownedTiles.length === 0 && !owned.isLoading ? (
              <div className="col-span-full rounded-xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
                Owned store has posts but returned no KPI tiles.
              </div>
            ) : null}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-sm font-semibold text-gray-900">Public · social</h2>
        {trackedKpis.length === 0 && !tracked.isLoading && !tracked.isError ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
            No public KPI tiles in this window. Tracked is social coverage only.
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {trackedKpis.map((kpi: ApiTrackedKpi) => (
              <KpiTile
                key={`public:${kpi.id}:${kpi.basis ?? 'none'}`}
                title={/social/i.test(kpi.label) ? kpi.label : `${kpi.label} · social`}
                basis={kpi.basis}
                change={kpi.change_pct}
                metricValue={kpi.value}
                value={formatKpiValue(kpi)}
                spark={[]}
                ratio={kpi.unit === 'ratio'}
                stroke="#059669"
              />
            ))}
            {trackedSeries.length > 0 ? (
              <Panel
                title={
                  <span className="flex flex-col gap-0.5">
                    <span>{tracked.data?.series?.metric ?? 'Public metric'} over time</span>
                    <span className="text-[11px] font-normal normal-case tracking-normal text-gray-400">
                      Public series from Tracked — not connected daily-metrics
                    </span>
                  </span>
                }
              >
                <KpiSpark
                  series={trackedSeries}
                  ratio={false}
                  name={tracked.data?.series?.metric ?? 'Value'}
                  stroke="#059669"
                />
              </Panel>
            ) : null}
          </div>
        )}
      </section>
    </>
  );

  if (embedded) return body;

  return (
    <MarketingPageShell
      title="KPIs"
      description="Connected and public marketing KPIs, side by side. Never averaged."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Radar', href: '/admin/marketing/radar' },
        { label: 'Performance', href: '/admin/marketing/radar/performance' },
        { label: 'KPIs' },
      ]}
      icon={Gauge}
      actions={rangeControls}
    >
      {body}
    </MarketingPageShell>
  );
};

export default KpisPage;
